import { useQuery } from '@tanstack/react-query';
import { useNavigate, useParams } from '@tanstack/react-router';
import { Form, Formik, setNestedObjectValues } from 'formik';
import { useEffect } from 'react';

import { MultipleOfferAvailability } from '../../../types/MultipleOfferAvailability';
import { PaymentTermReference } from '../../../types/PaymentTermReference';
import { PortMessage } from '../../../types/PortMessage';
import { ProductContext } from '../../../types/ProductContext';
import { SamplekitMessage } from '../../../types/SamplekitMessage';
import { SurchargeMessage } from '../../../types/SurchargeMessage';
import { Button } from '../../cdl/Button/Button';
import { FormikInput } from '../../cdl/Input/FormikInput';
import { ReadOnlyField } from '../../cdl/ReadOnlyField/ReadOnlyField';
import { Text } from '../../cdl/Text/Text';
import { useToasts } from '../../cdl/Toast/useToasts';
import { queries } from '../../common/api/queryKeys/queries';
import { Divider } from '../../common/Divider/Divider';
import { AdvancedError } from '../../common/Error/AdvancedError';
import { FormikDatePicker } from '../../common/form-elements/formik/FormikDatePicker';
import { FormikEnumOptionSelect } from '../../common/form-elements/formik/FormikEnumOptionSelect';
import { FormikFormattedNumberInput } from '../../common/FormattedNumberInput/FormikFormattedNumberInput';
import { FormikProductTable } from '../../common/FormikProductTable/FormikProductTable';
import { startOfTimeZoneDayAsUtcIso } from '../../common/helpers/dateFns.helper';
import { formatCompanyName } from '../../common/helpers/formatCompanyName.helper';
import { formatVessel } from '../../common/helpers/formatVessel.helper';
import { toNullableNumber } from '../../common/helpers/toNullableNumber.helper';
import { translate } from '../../common/helpers/translate.helper';
import { useRole } from '../../common/hooks/useRole';
import { Schedule, useSchedules } from '../../common/hooks/useSchedules';
import { LoadingIndicator } from '../../common/LoadingIndicator/LoadingIndicator';
import { ItemModel } from '../../common/models/ItemModel';
import { createOverlay } from '../../common/Overlay/Overlay';
import { OverlayHeader } from '../../common/Overlay/OverlayHeader';
import { Box } from '../../common/ui/Box';
import { theme } from '../../common/ui/theme';
import { OfferState } from '../../offer/model/OfferState';
import { FormikPortDetailSelect } from '../../port/portSelect/FormikPortDetailSelect';
import { useCreateAdditionalOffer } from '../hooks/useCreateAdditionalOffer';
import { useValidateRfqQuoteForm } from '../hooks/useValidateRfqQuoteForm';

export const RfqCreateQuotePage = () => {
    return createOverlay(<RfqCreateQuote />);
};

const RfqCreateQuote = () => {
    const { id } = useParams({ from: '/_app/requests-for-quotes/$id_/quote' });
    const navigate = useNavigate();
    const role = useRole();

    const rfqQuery = useQuery({
        ...queries.requestsForQuotes.detail(id),
    });

    const rfq = rfqQuery.data;
    const supplierSettingsQuery = useQuery({
        ...queries.suppliers.detail(rfq?.supplier?.id ?? '')._ctx.settings,
        enabled: role.isSupplier() && !!rfq?.supplier?.id,
    });
    const { schedules } = useSchedules({
        offerId: rfq?.offers[0]?.id,
        scheduleSharingEnabled:
            rfq?.scheduleSharingEnabled === true && supplierSettingsQuery.data?.scheduleSharingEnabled === true,
    });

    const createAdditionalOfferMutation = useCreateAdditionalOffer();
    const { addToast, addErrorToast } = useToasts();
    const isSpot = rfq?.offers.some((offer) => offer.isSpot()) ?? false;
    const formikValidate = useValidateRfqQuoteForm(isSpot);
    const isMultipleOfferAvailable = rfq ? rfq.multipleOfferAvailability === MultipleOfferAvailability.ENABLED : false;

    const handleCancel = () => {
        navigate({ to: '/requests-for-quotes/$id', params: { id } });
    };

    useEffect(() => {
        if (!rfq || rfq.multipleOfferAvailability === MultipleOfferAvailability.ENABLED) {
            return;
        }

        addErrorToast(translate('rfq.detail.additionalOfferUnavailableToast'));
        navigate({ to: '/requests-for-quotes/$id', params: { id } });
    }, [addErrorToast, id, navigate, rfq]);

    if (rfqQuery.isPending) {
        return (
            <OverlayHeader onClose={handleCancel}>
                <LoadingIndicator variant="page" />
            </OverlayHeader>
        );
    }

    if (rfqQuery.isError || !rfq) {
        return (
            <OverlayHeader title={translate('offer.quotationProcess.header.createQuote')} onClose={handleCancel}>
                <AdvancedError />
            </OverlayHeader>
        );
    }

    if (!isMultipleOfferAvailable) {
        return null;
    }

    const handlePortSelect =
        (setFieldValue: (field: string, value: unknown, shouldValidate?: boolean) => void) =>
        (port: PortMessage, scheduleRank?: number) => {
            if (!schedules?.length) return;

            const match = schedules.find(
                (entry: Schedule) => entry.port?.id === port.id && entry.rank === scheduleRank
            );

            if (match) {
                setFieldValue('dateDelivery', startOfTimeZoneDayAsUtcIso(match.eta, match.timeZoneId), false);
            }
        };

    const initialOffer = {
        state: OfferState.ENQUIRY,
        vessel: rfq.vessel,
        portId: rfq.port.id,
        dateDelivery: rfq.dateDelivery,
        items: rfq.items || [],
        surcharges: [],
        samplekits: rfq.samplekits || [],
        vendorReference: '',
        paymentTermReference: undefined,
        paymentTermReferenceDays: null,
        supplyMode: '',
        noticeDays: null,
        agentId: rfq.agentId,
        buyerReference: rfq.buyerReference || '',
    };

    const handleSubmit = async (values: typeof initialOffer) => {
        const quoteRequest = {
            buyerReference: values.buyerReference || undefined,
            agentId: values.agentId,
            state: OfferState.QUOTED,
            vesselId: values.vessel.id,
            portId: values.portId,
            dateDelivery: values.dateDelivery,
            noticeDays: values.noticeDays || undefined,
            paymentTermReference: values.paymentTermReference,
            paymentTermReferenceDays: toNullableNumber(values.paymentTermReferenceDays) || undefined,
            supplyMode: values.supplyMode,
            vendorReference: values.vendorReference || undefined,
            items: values.items.map((item: ItemModel) => ({
                productId: item.productId,
                units: item.units,
                unit: item.unit,
                unitSize: item.unitSize,
                packType: item.packType,
                deleted: item.deleted,
                volume: item.volume,
                price: item.price?.value
                    ? {
                          value: item.price.value,
                          currency: 'USD' as const,
                      }
                    : undefined,
            })),
            surcharges: values.surcharges.length
                ? values.surcharges.map((surcharge: SurchargeMessage) => ({
                      name: surcharge.name,
                      surchargeType: surcharge.surchargeType,
                      deleted: surcharge.deleted,
                      value: { value: Number(surcharge.value.value), currency: 'USD' as const },
                  }))
                : [],
            samplekits: values.samplekits.length
                ? values.samplekits.map((samplekit: SamplekitMessage) => ({
                      name: samplekit.name,
                      quantity: samplekit.quantity,
                      deleted: samplekit.deleted,
                      value: {
                          value: samplekit.value!.value,
                          currency: 'USD' as const,
                      },
                  }))
                : [],
        };

        try {
            await createAdditionalOfferMutation.mutateAsync({
                requestForQuoteId: id,
                offer: quoteRequest,
            });

            addToast(translate('offer.quotationProcess.sentSuccessfully'));
            navigate({ to: '/requests-for-quotes/$id', params: { id } });
        } catch {
            addErrorToast(translate('offer.quotationProcess.failedToSend'));
        }
    };

    return (
        <Box paddingBottom={40}>
            <Formik initialValues={initialOffer} validateOnMount onSubmit={handleSubmit} validate={formikValidate}>
                {(formikProps) => (
                    <Form>
                        <OverlayHeader
                            maxContentWidth={theme.maxContentWidths.wide}
                            title={translate('offer.quotationProcess.header.createQuote')}
                            onClose={handleCancel}
                            trailingVisual={
                                <Box display="flex" gap={8}>
                                    <Button type="button" onClick={handleCancel}>
                                        {translate('btn.cancel')}
                                    </Button>
                                    <Button
                                        type="button"
                                        onClick={async () => {
                                            await formikProps.submitForm();
                                            formikProps.setTouched(setNestedObjectValues(formikProps.errors, true));
                                        }}
                                        emphasis="high"
                                        isLoading={formikProps.isSubmitting}
                                    >
                                        {translate('offer.quotationProcess.navigationButtons.contracted.send')}
                                    </Button>
                                </Box>
                            }
                        >
                            <Box display="grid" padding={32} gap={48}>
                                <Box>
                                    <Text variant="title">
                                        {translate('offer.quotationProcess.coreData.banner.headline')}
                                    </Text>
                                    <Text variant="body" color="foreground.muted" as="p">
                                        {translate('offer.quotationProcess.coreData.banner.subHeadline')}
                                    </Text>

                                    <Divider marginTop={16} marginBottom={32} />

                                    <Box display="grid" gridTemplateColumns="repeat(3, 1fr)" gap={16}>
                                        <ReadOnlyField
                                            label={translate('offer.quotationProcess.labels.customer')}
                                            value={
                                                formatCompanyName({
                                                    company: rfq.customer,
                                                }) || ''
                                            }
                                        />

                                        <ReadOnlyField
                                            label={translate('offer.quotationProcess.labels.vessel')}
                                            value={formatVessel({ vessel: rfq.vessel }) || ''}
                                        />

                                        <FormikPortDetailSelect
                                            name="portId"
                                            label={translate('offer.quotationProcess.labels.port')}
                                            schedule={schedules}
                                            vesselId={rfq.vessel.id}
                                            onSelect={handlePortSelect(formikProps.setFieldValue)}
                                        />

                                        <FormikDatePicker
                                            name="dateDelivery"
                                            label={translate('offer.edit.lubes.deliveryDate')}
                                        />

                                        {isSpot ? (
                                            <Box display="grid" gridTemplateColumns="3fr 1fr" gap={8}>
                                                <FormikEnumOptionSelect
                                                    label={translate('offer.edit.lubes.paymentTerms')}
                                                    name="paymentTermReference"
                                                    enumType="paymentTermReference"
                                                    onOptionChange={(option) => {
                                                        if (option === PaymentTermReference.PREPAYMENT) {
                                                            formikProps.setFieldValue(
                                                                'paymentTermReferenceDays',
                                                                undefined
                                                            );
                                                        }
                                                    }}
                                                />
                                                <FormikFormattedNumberInput
                                                    name="paymentTermReferenceDays"
                                                    label={translate('offer.edit.lubes.paymentTermsDays')}
                                                    allowNegativeValue={false}
                                                    allowDecimals={false}
                                                    disabled={
                                                        formikProps.values.paymentTermReference ===
                                                        PaymentTermReference.PREPAYMENT
                                                    }
                                                />
                                            </Box>
                                        ) : null}

                                        <FormikFormattedNumberInput
                                            name="noticeDays"
                                            label={translate('offer.edit.lubes.noticeDays')}
                                            allowNegativeValue={false}
                                            allowDecimals={false}
                                            markAsOptional
                                        />
                                        <FormikEnumOptionSelect
                                            name="supplyMode"
                                            enumType="supplyMode"
                                            label={translate('offer.edit.lubes.supplyMode')}
                                            context={ProductContext.LUBES}
                                        />
                                        <FormikInput
                                            name="vendorReference"
                                            label={translate('offer.edit.lubes.vendorReference')}
                                        />
                                    </Box>
                                </Box>

                                <Box>
                                    <Text variant="title">
                                        {translate('offer.quotationProcess.products.banner.headline')}
                                    </Text>
                                    <Text variant="body" color="foreground.muted" as="p">
                                        {translate('offer.quotationProcess.products.banner.subHeadline')}
                                    </Text>

                                    <Divider marginTop={16} marginBottom={32} />

                                    <FormikProductTable
                                        config={{
                                            initialItems: rfq.items || [],
                                            showPrices: true,
                                            editable: true,
                                            editSurcharges: true,
                                            editPrices: true,
                                        }}
                                    />
                                </Box>
                            </Box>
                        </OverlayHeader>
                    </Form>
                )}
            </Formik>
        </Box>
    );
};
